/**
 * WorkspaceSidebar — Contextual sidebar rendered inside Layout.tsx
 * when the current route belongs to a workspace (Patient Details,
 * Hospital dashboard, HCP dashboard).
 *
 * Replaces the global admin navigation with a compact, operational
 * menu containing only the modules relevant to that workspace.
 *
 * Specs:
 *   Expanded width : 220px (w-[220px])
 *   Collapsed width: 56px  (w-[56px])
 *   Active state   : primary tinted bg + left indicator
 *   Icons          : muted-foreground, active → primary
 *   Responsive     : persistent on desktop, collapsible on tablet,
 *                    overlay drawer on mobile
 */

import { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/modules/shared/components/ui/button';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { SidebarFooter } from '@/modules/shared/components/ui/SidebarFooter';
import { useSidebarCollapse } from '@/modules/shared/hooks/useSidebarCollapse';
import {
  Menu,
  X,
} from 'lucide-react';
import type { WorkspaceNavConfig, WorkspaceNavItem } from '@/modules/shared/constants/workspaceNavigation';

// ─── Props ───────────────────────────────────────────────────

interface WorkspaceSidebarProps {
  config: WorkspaceNavConfig;
  /** Programmatic base path (fully resolved, no builder needed) */
  basePath: string;
  /** Label for the back-link at top (e.g. "Back to Workspace") */
  backLabel?: string;
  /** Path for the back link */
  backPath?: string;
  /** Items to hide based on permissions */
  hiddenKeys?: Set<string>;
  /** Runtime badge overrides */
  badges?: Partial<Record<string, number>>;
  /** Program ID for legal page links in footer */
  programId?: string;
}

// ─── Component ───────────────────────────────────────────────

export function WorkspaceSidebar({
  config,
  basePath,
  backLabel: _backLabel = 'Back',
  backPath: _backPath,
  hiddenKeys,
  badges,
  programId,
}: WorkspaceSidebarProps) {
  const [collapsed, toggleCollapse] = useSidebarCollapse('wsSidebarCollapsed');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const visibleItems = hiddenKeys
    ? config.items.filter(item => !hiddenKeys.has(item.key))
    : config.items;

  // ── Determine navigation mode ─────────────────────────────
  // All workspaces now use segment-based child routes
  // ── Active state detection ──────────────────────────────────
  const getActiveKey = useCallback((): string => {
    // Match last segment of pathname against basePath
    const relative = location.pathname.replace(basePath, '').replace(/^\//, '');
    return relative || 'overview';
  }, [basePath, location.pathname]);

  const activeKey = getActiveKey();

  // ── Nav item renderer ──────────────────────────────────────

  const renderItem = (item: WorkspaceNavItem, onClose?: () => void) => {
    const isActive = item.key === activeKey;
    const badge = badges?.[item.key] ?? item.badge;

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      // Navigate to child route segment
      const target = item.key === 'overview' ? basePath : `${basePath}/${item.key}`;
      navigate(target);
      onClose?.();
    };

    const linkContent = (
      <button
        key={item.key}
        type="button"
        onClick={handleClick}
        data-tooltip={item.label}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'ws-nav-item',
          'transition-all duration-[180ms] cursor-pointer select-none outline-none',
          collapsed ? 'ws-nav-item--collapsed' : '',
          isActive
            ? 'ws-nav-item--active'
            : 'ws-nav-item--idle',
        )}
      >

        <span className="ws-nav-icon">
          <AppIcon icon={item.icon} size="compact" variant="inherit" />
        </span>

        {!collapsed && (
          <span className="ws-nav-label">
            {item.label}
          </span>
        )}

        {!collapsed && badge != null && badge > 0 && (
          <span className="ml-auto text-[11px] font-semibold bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">
            {badge}
          </span>
        )}
      </button>
    );

    return linkContent;
  };

  // ── Sidebar shell ──────────────────────────────────────────

  const sidebarContent = (onClose?: () => void) => (
    <>
      {/* Title — hidden when config.hideTitle is set */}
      {!collapsed && !config.hideTitle && (
        <div className="ws-nav-section-title">
          {config.title}
        </div>
      )}

      {/* Nav items */}
      <nav className="ws-nav-list" aria-label={config.title}>
        {visibleItems.map(item => renderItem(item, onClose))}
      </nav>
    </>
  );

  return (
    <>
      {/* Mobile Scrim */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile hamburger trigger (shown only on mobile, positioned in the content area) */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed bottom-4 left-4 z-30 md:hidden h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center"
        aria-label="Open workspace menu"
      >
        <Menu className="h-4 w-4" />
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          'app-sidebar ws-sidebar',
          // Desktop
          'hidden md:flex',
          collapsed && 'is-collapsed',
          collapsed ? 'w-[56px] min-w-[56px]' : 'w-[200px] min-w-[200px]',
        )}
      >
        {/* Unified collapse toggle */}
        <button
          type="button"
          onClick={toggleCollapse}
          data-tooltip={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="sidebar-collapse-handle"
        >
          <span className="sidebar-collapse-grip" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>

        {/* Scrollable nav — overflow:visible when collapsed so CSS tooltip pseudo-elements
            are not clipped (overflow-y:auto forces overflow-x to be non-visible per spec) */}
        <div className={cn('flex-1', collapsed ? 'overflow-visible' : 'overflow-y-auto py-1')}>
          {sidebarContent()}
        </div>

        {/* Footer */}
        <SidebarFooter
          programId={programId}
          collapsed={collapsed}
        />
      </aside>

      {/* Mobile drawer */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full z-50 flex flex-col',
          'bg-card dark:bg-sidebar border-r border-border shadow-xl',
          'w-[260px] transition-transform duration-300 ease-in-out',
          'md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Mobile close */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-border/60">
          <span className="text-[12px] font-semibold text-foreground">{config.title}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable nav */}
        <div className="flex-1 overflow-y-auto py-3">
          {sidebarContent(() => setMobileOpen(false))}
        </div>

        {/* Footer */}
        <SidebarFooter
          programId={programId}
        />
      </aside>
    </>
  );
}
